import React, { useState } from "react";
import { motion } from "framer-motion";
import { BRAND } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactCTA() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const fd = new FormData(e.currentTarget);
    const payload = {
      name: (fd.get("name") || "").toString().trim(),
      studio: (fd.get("studio") || "").toString().trim(),
      email: (fd.get("email") || "").toString().trim(),
      country: (fd.get("country") || "").toString().trim(),
      project_type: (fd.get("type") || "").toString(),
      budget: (fd.get("budget") || "").toString(),
      timeline: (fd.get("timeline") || "").toString(),
      deck: (fd.get("deck") || "").toString(),
      brief: (fd.get("brief") || "").toString(),
    };
    try {
      const r = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `Request failed (${r.status})`);
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative vw-section overflow-hidden" data-testid="vw-contact">
      {/* Ambient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 vw-grid opacity-30" />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, var(--vw-blue), transparent 60%)" }}
        />
      </div>

      <div className="relative vw-container">
        <div className="mb-14 md:mb-20 max-w-4xl">
          <div className="vw-eyebrow mb-6">30-MIN PRODUCER CALL · NDA-FIRST</div>
          <h2 className="font-display text-6xl md:text-7xl lg:text-[8vw] leading-[0.88] tracking-tighter">
            Get a shot-by-shot
            <br />
            <span className="vw-text-gradient">budget on your feature</span>
            <span className="text-[var(--vw-cyan)]">.</span>
          </h2>
          <p className="mt-8 font-serif-italic text-2xl md:text-3xl text-[var(--vw-text-dim)] max-w-[38ch] leading-[1.25]">
            30 minutes with our line producer. Shot count, budget, delivery date. You leave with a real number — not a marketing quote.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-12 lg:gap-20">
          {/* Form */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="vw-glass p-8 md:p-10 border border-[var(--vw-border-strong)]"
            data-testid="vw-contact-form"
          >
            {submitted ? (
              <div className="min-h-[420px] flex flex-col items-start justify-center gap-6" data-testid="vw-contact-submitted">
                <div className="w-14 h-14 grid place-items-center border-2 border-[var(--vw-cyan)] rounded-full vw-glow-cyan">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-[var(--vw-cyan)]"><path d="M5 12l5 5L20 7"/></svg>
                </div>
                <h3 className="font-display text-3xl md:text-4xl leading-tight">Received. We'll be in touch in one business day.</h3>
                <p className="text-[var(--vw-text-dim)] max-w-md leading-relaxed">
                  Meanwhile — feel free to review the pipeline and portfolio. For urgent requests, our LA representation team is available directly at <a href={`mailto:${BRAND.usaEmail}`} className="text-[var(--vw-cyan)] underline underline-offset-4">{BRAND.usaEmail}</a>.
                </p>
                <button type="button" onClick={() => setSubmitted(false)} className="vw-btn-ghost mt-2">
                  Submit Another Request
                </button>
              </div>
            ) : (
              <>
                <div className="mb-8">
                  <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-2">STEP 01 · TELL US ABOUT YOUR PROJECT</div>
                  <div className="h-[2px] w-24 bg-gradient-to-r from-[var(--vw-cyan)] to-[var(--vw-blue)]" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <FormField label="Full Name" name="name" required data-testid="vw-form-name" />
                  <FormField label="Studio / Company" name="studio" required data-testid="vw-form-studio" />
                  <FormField label="Executive Email" name="email" type="email" required data-testid="vw-form-email" />
                  <FormField label="Country" name="country" required data-testid="vw-form-country" />
                  <FormSelect label="Project Type" name="type" testId="vw-form-type" options={[
                    "Animated Feature", "Animated Series", "VFX / Hybrid", "Original IP / Co-Prod", "Pilot / Sizzle", "Other",
                  ]} />
                  <FormSelect label="Budget Range" name="budget" testId="vw-form-budget" options={[
                    "< $500K", "$500K – $2M", "$2M – $5M", "$5M – $15M", "$15M+", "Confidential",
                  ]} />
                  <FormSelect label="Timeline" name="timeline" testId="vw-form-timeline" options={[
                    "In pre-production", "Green-lit, starting soon", "In active production", "Exploring options",
                  ]} />
                  <FormField label="Deck / Reference URL" name="deck" placeholder="Optional" data-testid="vw-form-deck" />
                </div>

                <div className="mt-5">
                  <label className="block font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-2">
                    Project Brief
                  </label>
                  <textarea
                    name="brief"
                    rows={4}
                    placeholder="Runtime, genre, comparables, target platform, any hard constraints..."
                    className="w-full bg-black/30 border border-[var(--vw-border-strong)] px-4 py-3 text-[var(--vw-text)] placeholder:text-[var(--vw-text-mute)] focus:border-[var(--vw-cyan)] outline-none transition-colors resize-none"
                    data-testid="vw-form-brief"
                  />
                </div>

                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] flex items-center gap-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 2l9 4v6c0 5.5-4 10-9 12-5-2-9-6.5-9-12V6l9-4z"/></svg>
                    NDA-first · Encrypted transit · No AI training on your data
                  </p>
                  <button
                    type="submit"
                    className="vw-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="vw-form-submit"
                    disabled={submitting}
                  >
                    {submitting ? "Sending…" : "Send Me a Budget"}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                  </button>
                </div>

                {error && (
                  <div
                    className="mt-5 border border-[var(--vw-orange)]/40 bg-[color:rgba(255,138,71,0.06)] px-4 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--vw-orange)]"
                    data-testid="vw-form-error"
                  >
                    {error}. Please email <a className="underline" href={`mailto:${BRAND.usaEmail}`}>{BRAND.usaEmail}</a> directly.
                  </div>
                )}
              </>
            )}
          </motion.form>

          {/* Right column — direct lines */}
          <div className="flex flex-col gap-8">
            <div>
              <div className="vw-eyebrow mb-5">DIRECT LINES</div>
              <div className="space-y-6">
                <ContactRow
                  region="US · Los Angeles"
                  detail="Lampost Films"
                  email={BRAND.usaEmail}
                  phone={BRAND.usaPhone}
                />
                <ContactRow
                  region="India · Mumbai · HQ"
                  detail="Production & Delivery"
                  email={BRAND.contactEmail}
                />
              </div>
            </div>

            <div className="border-t border-[var(--vw-border)] pt-8">
              <div className="vw-eyebrow mb-5">STUDIOS</div>
              <div className="space-y-4 font-mono text-[11px] text-[var(--vw-text-dim)] leading-relaxed">
                {BRAND.offices.map((o) => (
                  <div key={o.region} className="border-l border-[var(--vw-border)] pl-4">
                    <div className="text-[var(--vw-cyan)] uppercase tracking-[0.24em] mb-1">{o.region}</div>
                    <div>{o.address}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-[var(--vw-border)] pt-8 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]">
              <span className="w-1.5 h-1.5 bg-[var(--vw-cyan)] vw-blink" />
              Currently accepting Q1 2026 productions
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FormField({ label, name, type = "text", required = false, placeholder = "", ...rest }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-2">
        {label}{required && <span className="text-[var(--vw-cyan)]"> *</span>}
      </div>
      <input
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        className="w-full bg-black/30 border border-[var(--vw-border-strong)] px-4 py-3 text-[var(--vw-text)] placeholder:text-[var(--vw-text-mute)] focus:border-[var(--vw-cyan)] outline-none transition-colors"
        {...rest}
      />
    </label>
  );
}

function FormSelect({ label, name, options, testId }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-2">
        {label}
      </div>
      <select
        name={name}
        data-testid={testId}
        defaultValue=""
        className="w-full bg-black/30 border border-[var(--vw-border-strong)] px-4 py-3 text-[var(--vw-text)] focus:border-[var(--vw-cyan)] outline-none transition-colors appearance-none cursor-pointer"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2300E5FF' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 14px center",
        }}
      >
        <option value="" disabled>Select...</option>
        {options.map((o) => <option key={o} value={o} className="bg-[var(--vw-surface)]">{o}</option>)}
      </select>
    </label>
  );
}

function ContactRow({ region, detail, email, phone }) {
  return (
    <div className="border-l-2 border-[var(--vw-cyan)] pl-5">
      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] mb-1">
        {region}
      </div>
      <div className="font-display text-lg text-[var(--vw-text)] mb-2">{detail}</div>
      <div className="space-y-1">
        <a href={`mailto:${email}`} className="block text-sm text-[var(--vw-text-dim)] hover:text-[var(--vw-cyan)]">
          {email}
        </a>
        {phone && (
          <a href={`tel:${phone.replace(/\s+/g, "")}`} className="block font-mono text-sm text-[var(--vw-text-dim)] hover:text-[var(--vw-cyan)]">
            {phone}
          </a>
        )}
      </div>
    </div>
  );
}
